import { useEffect, useMemo, useState } from "react";
import type { BackgroundImageFraming, OnBodyKind, Vec3 } from "../../../shared/api-contract";
import { useViewer } from "../../../state/ViewerProvider";
import { useEditorStore, useUi } from "../../../state/editor/useEditor";
import { getModel } from "../../../features/vault/vaultApi";
import { pickFile } from "../../../util/download";
import { Icon } from "../../kit";
import { CompactCheck, CompactNumber, CompactSelect, CompactSlider, MiniBtn, RecordOff, Row, RowInset } from "../inspector/rows";
import { useManifest, type ManifestEntry } from "../library/useManifest";
import { useCaps } from "../CapsProvider";
import { useStudioModelId } from "../studioModel";
import { ManualOverlay } from "../onbody/ManualOverlay";
import { devTestForced, useDevTestPhotos, usePlacements } from "../onbody/library";
import { bodyPartFor, findPlacement, guessPieceKind, type FoundPlacement } from "../onbody/placements";
import { ALONG_RANGE, FINGER_NAMES, fingersOf, type FingerName } from "../onbody/anchorMath";
import { FIT_RANGE, WIDTH_SCALE_RANGE, onBodySessionFor, useOnBody, type OnBodySession, type OnBodyState } from "../onbody/session";

/**
 * On Body — the piece worn in a photograph: a ring on the finger, a necklace on
 * the neck (viewer contract v63).
 *
 * Picking a photo sets it as the backdrop (whole — contain, as iJewel shows it)
 * and hands the viewer the chosen finger's anchor: knuckle, middle joint, where
 * along it the ring sits, its width measured there, its true direction and the
 * back-of-hand normal — every hand in the library was measured ahead of time
 * (`public/onbody/placements.json`, v2). The viewer fixes the camera, fits the
 * piece onto the finger at the right size, turns its head with the hand, hides
 * the far half of the band behind a depth-only finger and shades the skin
 * beside it. A photo of the user's own is searched for hands in the browser;
 * anything not found is placed with two clicks, and measured from the photo.
 *
 * Fit & Placement follows iJewel's controls: Switch Hand, the finger picker,
 * Along Finger (0–2, default 0.65), Fit, plus the stone-faces-camera switch
 * and the contact shadow (viewer ≥ v64), Adjust and Auto fit.
 *
 * The session (photo, anchor, the backdrop to put back) lives in
 * `onbody/session.ts`, not here: On Body stays on when this panel closes.
 * Leaving the panel does not clear it — the user may be exporting the picture.
 *
 * On a viewer older than v63 this is what it always was: a backdrop picker,
 * with a Clear that puts the previous backdrop back.
 */
export function OnBodyPanel() {
  const { client } = useViewer();
  const caps = useCaps();
  const store = useEditorStore();
  const [ui] = useUi();
  const session = useMemo(() => onBodySessionFor(client), [client]);
  const s = useOnBody(session);
  const v63 = caps.onBodyV63;
  const [pending, setPending] = useState<string | null>(null);

  // ── the photos ────────────────────────────────────────────────────────────
  const { items, urlFor, thumbFor } = useManifest(null, "onBody");
  const forced = devTestForced();
  const dev = useDevTestPhotos(items.length === 0 || forced);
  const index = usePlacements();
  const useDev = dev.length > 0 && (forced || items.length === 0);
  const sources: { ref: ManifestEntry; url: string; thumb: string }[] = useDev
    ? dev.map((d) => ({ ref: d, url: d.url, thumb: d.thumbUrl }))
    : items.map((i) => ({ ref: i, url: urlFor(i), thumb: thumbFor(i) || urlFor(i) }));
  const photos: PanelPhoto[] = sources.map(({ ref, url, thumb }) => ({
    id: ref.id,
    label: ref.label,
    url,
    thumb,
    found: index ? findPlacement(index, ref) : null,
  }));

  // ── Ring or Necklace, guessed from the Vault model ───────────────────────
  const modelId = useStudioModelId();
  useEffect(() => {
    if (!modelId) return;
    let alive = true;
    getModel(modelId)
      .then((m) => {
        if (alive) session.suggestKind(bodyPartFor(guessPieceKind(`${m.category ?? ""} ${m.name} ${m.tags.join(" ")}`)));
      })
      .catch(() => { /* no Vault model to read: stay on Ring */ });
    return () => {
      alive = false;
    };
  }, [modelId, session]);

  // Clicks are only collected while this panel is open.
  useEffect(() => () => { if (session.getState().manual) session.cancelManual(); }, [session]);

  // Every backdrop write switches the ACTIVE background source; re-seed the
  // scene slice so the Background panel and the inspector agree with it.
  const resync = () => void store.refresh("scene");

  const pick = async (p: PanelPhoto) => {
    setPending(p.id);
    try {
      await session.pickPhoto({ id: p.id, label: p.label, url: p.url, placement: p.found?.placement ?? null });
    } finally {
      setPending(null);
      resync();
    }
  };

  const own = () =>
    void pickFile("image/*", async (f) => {
      setPending("own");
      try {
        await session.useOwnPhoto(f);
      } finally {
        setPending(null);
        resync();
      }
    });

  const clear = async () => {
    await session.clear();
    resync();
  };

  const locked = s.busy === "photo" || s.busy === "detect" || s.busy === "clear";

  return (
    <RecordOff>
      <RowInset.Provider value="flush">
        <div className="flex flex-col gap-3 p-3" data-onbody-panel>
          {v63 ? <KindSwitch kind={s.kind} onChange={(k) => void session.setKind(k)} /> : null}

          {v63 && (s.status || s.busy === "detect") ? <StatusLine s={s} /> : null}

          <div className="flex flex-wrap gap-1.5">
            <MiniBtn icon="upload" onClick={own} busy={pending === "own"} disabled={locked && pending !== "own"}>
              Use your own photo
            </MiniBtn>
            {v63 && s.photo ? (
              <MiniBtn icon="touch_app" onClick={() => void session.startManual()} active={!!s.manual} disabled={locked}>
                Place by hand
              </MiniBtn>
            ) : null}
            <MiniBtn icon="hide_image" onClick={() => void clear()} disabled={!s.photo || locked} busy={s.busy === "clear"}>
              Clear
            </MiniBtn>
          </div>

          {s.error ? <span className="text-[11px] leading-snug text-ed-red">{s.error}</span> : null}

          {/* Only while the shown kind is actually placed: after a switch to
              Necklace on a photo with no neck, Fit would move nothing. */}
          {v63 && s.placed && session.anchorNow() ? <FitAndAdjust session={session} s={s} v64={caps.onBodyV64} /> : null}

          {s.photo && caps.backgroundFramingV49 ? <PhotoFraming session={session} /> : null}

          {!v63 ? <LegacyViewpoint /> : null}

          <PhotoGrid
            photos={photos}
            kind={v63 ? s.kind : "finger"}
            activeId={s.photo?.key ?? null}
            pendingId={pending}
            locked={locked}
            view={ui.libraryView}
            onPick={(p) => void pick(p)}
          />

          {v63 ? <ManualOverlay session={session} /> : null}
        </div>
      </RowInset.Provider>
    </RecordOff>
  );
}

interface PanelPhoto {
  id: string;
  label: string;
  url: string;
  thumb: string;
  found: FoundPlacement | null;
}

function KindSwitch({ kind, onChange }: { kind: OnBodyKind; onChange: (k: OnBodyKind) => void }) {
  const opts: [OnBodyKind, string, string][] = [
    ["finger", "Ring", "Wear the piece on the ring finger"],
    ["neck", "Necklace", "Wear the piece around the neck"],
  ];
  return (
    <div role="radiogroup" aria-label="Wear it as" className="flex h-[34px] items-center rounded-lg bg-ed-bg p-[3px]" data-onbody-kind>
      {opts.map(([k, word, tip]) => {
        const on = kind === k;
        return (
          <button
            key={k}
            type="button"
            role="radio"
            aria-checked={on}
            title={tip}
            onClick={() => onChange(k)}
            className={`h-7 flex-1 rounded-md px-3 text-[13px] font-medium transition-colors ${
              on ? "bg-ed-panel text-ed-text shadow-[0_1px_2px_rgba(0,0,0,0.1)]" : "text-ed-text-2 hover:text-ed-text"
            }`}
          >
            {word}
          </button>
        );
      })}
    </div>
  );
}

function StatusLine({ s }: { s: OnBodyState }) {
  const busy = s.busy === "detect" || s.busy === "photo" || s.busy === "place";
  const status = s.status ?? { tone: "info" as const, text: "Working…" };
  const icon = busy ? "progress_activity" : status.tone === "ok" ? "check_circle" : status.tone === "warn" ? "error" : "info";
  return (
    <div
      className="flex items-start gap-2 rounded-lg border border-ed-border bg-ed-panel-2 px-2.5 py-2 text-[12px] leading-snug text-ed-text"
      data-onbody-status={status.tone}
      aria-live="polite"
    >
      <Icon
        name={icon}
        size={16}
        className={`mt-px shrink-0 ${busy ? "animate-spin text-ed-text-3" : status.tone === "ok" ? "text-ed-accent" : status.tone === "warn" ? "text-ed-red" : "text-ed-text-3"}`}
      />
      <span>{status.text}</span>
    </div>
  );
}

const fmt = (v: number, d = 3) => Number(v.toFixed(d));

const FINGER_LABEL: Record<FingerName, string> = { thumb: "Thumb", index: "Index", middle: "Middle", ring: "Ring", pinky: "Pinky" };

/** Fit & Placement: hand, finger, along, fit, the look rows, Auto fit, and the
 *  Adjust mode with the tinted finger. */
function FitAndAdjust({ session, s, v64 }: { session: OnBodySession; s: OnBodyState; v64: boolean }) {
  const range = FIT_RANGE[s.kind];
  const node = s.node;
  const size = node ? node.scale.x / (s.baseScale || 1) : 1;
  const finger = s.kind === "finger";
  const hands = s.photo?.hands ?? [];
  const hand = hands[s.handIndex] ?? hands[0];
  const have = hand ? fingersOf(hand) : [];
  const shown: FingerName = have.includes(s.finger) ? s.finger : (have[0] ?? s.finger);
  return (
    <div className="flex flex-col gap-1 border-t border-ed-border pt-2" data-onbody-adjust={s.setup ? "open" : "closed"}>
      <span className="text-[12px] font-semibold text-ed-text-2">Fit &amp; Placement</span>
      {finger && hands.length > 1 ? (
        <div className="flex items-center justify-between gap-2 py-1" data-onbody-hands={hands.length}>
          <span className="text-[12px] text-ed-text-2">Switch hand</span>
          <MiniBtn icon="swap_horiz" onClick={() => void session.switchHand()}>
            {`Hand ${s.handIndex + 1} of ${hands.length}`}
          </MiniBtn>
        </div>
      ) : null}
      {finger ? (
        <div role="radiogroup" aria-label="Finger" className="flex h-[30px] items-center rounded-lg bg-ed-bg p-[3px]" data-onbody-finger>
          {FINGER_NAMES.map((f) => {
            const on = shown === f;
            const can = have.includes(f);
            return (
              <button
                key={f}
                type="button"
                role="radio"
                aria-checked={on}
                disabled={!can}
                title={can ? `Wear it on the ${FINGER_LABEL[f].toLowerCase()} finger` : "This hand was placed by hand on one finger"}
                onClick={() => void session.setFinger(f)}
                data-finger={f}
                className={`h-6 flex-1 rounded-md px-1 text-[11.5px] font-medium transition-colors ${
                  on ? "bg-ed-panel text-ed-text shadow-[0_1px_2px_rgba(0,0,0,0.1)]" : can ? "text-ed-text-2 hover:text-ed-text" : "text-ed-text-3 opacity-50"
                }`}
              >
                {FINGER_LABEL[f]}
              </button>
            );
          })}
        </div>
      ) : null}
      {finger ? (
        <Row label="Along finger" hint="Where the ring sits: 0 is the knuckle, 1 the middle joint.">
          <CompactSlider
            value={s.along}
            min={ALONG_RANGE.min}
            max={ALONG_RANGE.max}
            step={0.01}
            format={(v) => v.toFixed(2)}
            onChange={(v) => session.setAlong(v)}
          />
        </Row>
      ) : null}
      <Row label="Fit" hint={s.kind === "neck" ? "How loosely the chain sits around the neck." : "How snug the ring sits: lower is tighter on the finger."}>
        <CompactSlider
          value={s.fit[s.kind]}
          min={range.min}
          max={range.max}
          step={0.01}
          format={(v) => `${v.toFixed(2)}×`}
          onChange={(v) => session.setFit(v)}
        />
      </Row>
      {finger && s.photo?.widthUnmeasured ? (
        <Row label="Finger width" hint="This photo's pixels could not be read, so the width is a guess from the joints — match it to the finger.">
          <CompactSlider
            value={s.widthScale}
            min={WIDTH_SCALE_RANGE.min}
            max={WIDTH_SCALE_RANGE.max}
            step={0.01}
            format={(v) => `${v.toFixed(2)}×`}
            onChange={(v) => session.setWidthScale(v)}
          />
        </Row>
      ) : null}
      {finger && v64 ? (
        <Row label="Stone faces camera" hint="Show the stone whichever way the hand is turned. Off, the ring turns with the hand — on a palm, the stone is behind the finger.">
          <CompactCheck checked={s.headToCamera} onChange={(v) => void session.setHeadToCamera(v)} />
        </Row>
      ) : null}
      {finger && v64 ? (
        <Row label="Contact shadow" hint="The soft shade where the ring meets the skin.">
          <CompactSlider
            value={s.contactShadow}
            min={0}
            max={1}
            step={0.01}
            format={(v) => v.toFixed(2)}
            onChange={(v) => void session.setContactShadow(v)}
          />
        </Row>
      ) : null}
      <div className="flex flex-wrap gap-1.5 pt-1">
        {s.setup ? null : (
          <MiniBtn icon="tune" onClick={() => void session.openAdjust()}>
            Adjust
          </MiniBtn>
        )}
        <MiniBtn icon="fit_screen" onClick={() => void session.autoFit()} busy={s.busy === "place"}>
          Auto fit
        </MiniBtn>
      </div>

      {s.setup && node ? (
        <div className="mt-1 flex flex-col gap-0.5 rounded-lg border border-ed-border bg-ed-row px-2.5 pb-2.5 pt-1.5">
          <p className="pb-1 text-[11px] leading-snug text-ed-text-3">
            The blue tube is the {s.kind === "neck" ? "neck" : "finger"} the piece is fitted to; it hides the far side of
            the piece. Press Done to hide the tint before you export.
          </p>
          <Row label="Model size" hint="A multiple of the size it was placed at.">
            <CompactNumber value={fmt(size)} min={0.2} max={5} step={0.01} onChange={(v) => void session.setSize(v)} width={64} />
          </Row>
          <AxisRow label="Position" value={node.position} step={s.posStep} onChange={(a, v) => void session.setPosition(a, v)} />
          <AxisRow label="Rotation" unit="°" value={node.rotationDeg} step={1} digits={1} onChange={(a, v) => void session.setRotation(a, v)} />
          <div className="flex items-center gap-1.5 pt-1.5" data-onbody-turns>
            <span className="mr-auto text-[12px] text-ed-text-2" title="A quarter turn about the screen's axes, around the ring's hole">
              Rotate
            </span>
            {(["x", "y", "z"] as const).map((a) => (
              <MiniBtn key={a} onClick={() => void session.rotate90(a)}>
                {`${a.toUpperCase()}\u00a090°`}
              </MiniBtn>
            ))}
          </div>
          <div className="flex justify-end pt-2">
            <MiniBtn primary onClick={() => void session.closeAdjust()}>
              Done
            </MiniBtn>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function AxisRow({
  label,
  value,
  step,
  unit,
  digits = 3,
  onChange,
}: {
  label: string;
  value: Vec3;
  step: number;
  unit?: string;
  digits?: number;
  onChange: (axis: keyof Vec3, v: number) => void;
}) {
  return (
    <div className="flex flex-col gap-1 py-1" data-onbody-axis={label.toLowerCase()}>
      <span className="text-[12px] leading-[16px] text-ed-text-2">
        {label}
        {unit ? <span className="text-ed-text-3"> ({unit})</span> : null}
      </span>
      <div className="grid grid-cols-3 gap-1.5">
        {(["x", "y", "z"] as const).map((a) => (
          <label key={a} className="flex items-center gap-1">
            <span className="sr-only">{label} </span>
            <span className="w-3 shrink-0 text-[11px] font-medium uppercase text-ed-text-3">{a}</span>
            <CompactNumber value={fmt(value[a], digits)} step={step} onChange={(v) => onChange(a, v)} width={62} />
          </label>
        ))}
      </div>
    </div>
  );
}

/** The photo's own fit / zoom / pan. Moving the photo moves the finger, so
 *  the session re-places after each change. */
function PhotoFraming({ session }: { session: OnBodySession }) {
  const { client } = useViewer();
  const [open, setOpen] = useState(false);
  const [framing, setFraming] = useState<BackgroundImageFraming | null>(null);
  useEffect(() => {
    if (!open) return;
    let alive = true;
    void client.ring.getBackgroundImageFraming().then((f) => alive && setFraming(f)).catch(() => {});
    return () => {
      alive = false;
    };
  }, [open, client]);
  const patch = (p: Partial<BackgroundImageFraming>) => {
    setFraming((f) => ({ ...(f ?? {}), ...p }));
    void session.frame(p).then((f) => f && setFraming(f)).catch(() => {});
  };
  return (
    <div className="flex flex-col border-t border-ed-border pt-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex items-center justify-between text-[12px] font-semibold text-ed-text-2 hover:text-ed-text"
      >
        Photo framing
        <Icon name={open ? "expand_less" : "expand_more"} size={16} />
      </button>
      {open && framing ? (
        <div className="flex flex-col pt-1">
          <Row label="Fill">
            <CompactSelect
              value={framing.fit ?? "cover"}
              options={[
                { id: "cover", label: "Cover" },
                { id: "contain", label: "Contain" },
                { id: "stretch", label: "Stretch" },
              ]}
              onChange={(v) => patch({ fit: v as BackgroundImageFraming["fit"] })}
            />
          </Row>
          <Row label="Zoom">
            <CompactSlider value={framing.scale ?? 1} min={0.5} max={3} step={0.01} onChange={(v) => patch({ scale: v })} />
          </Row>
          <Row label="Pan X">
            <CompactSlider value={framing.offsetX ?? 0} min={-1} max={1} step={0.01} onChange={(v) => patch({ offsetX: v })} />
          </Row>
          <Row label="Pan Y">
            <CompactSlider value={framing.offsetY ?? 0} min={-1} max={1} step={0.01} onChange={(v) => patch({ offsetY: v })} />
          </Row>
        </div>
      ) : null}
    </div>
  );
}

/** Pre-v63 viewers: the camera is free, so the old view buttons stay. */
function LegacyViewpoint() {
  const { client } = useViewer();
  const r = client.ring;
  return (
    <div className="flex flex-col gap-2 border-t border-ed-border pt-2">
      <span className="text-[12px] font-semibold text-ed-text-2">Viewpoint</span>
      <div className="flex flex-wrap gap-1.5">
        <MiniBtn onClick={() => void r.setCameraPreset("front")}>Front</MiniBtn>
        <MiniBtn onClick={() => void r.setCameraPreset("top")}>Top</MiniBtn>
        <MiniBtn onClick={() => void r.setCameraPreset("right")}>Side</MiniBtn>
        <MiniBtn icon="fit_screen" onClick={() => void r.autoFit()}>Auto fit</MiniBtn>
      </div>
    </div>
  );
}

function PhotoGrid({
  photos,
  kind,
  activeId,
  pendingId,
  locked,
  view,
  onPick,
}: {
  photos: PanelPhoto[];
  kind: OnBodyKind;
  activeId: string | null;
  pendingId: string | null;
  locked: boolean;
  view: "card" | "list";
  onPick: (p: PanelPhoto) => void;
}) {
  // In Necklace mode the photos with a neck come first, the rest dimmed: a
  // necklace can still go on one of them, by hand.
  const hasNeck = (p: PanelPhoto) => !!p.found?.placement.neck;
  const list = kind === "neck" ? [...photos.filter(hasNeck), ...photos.filter((p) => !hasNeck(p))] : photos;
  if (!photos.length) {
    return <p className="text-[12px] leading-snug text-ed-text-3">No photos in the library yet — use your own.</p>;
  }
  return (
    <div className="flex flex-col gap-2 border-t border-ed-border pt-2">
      <div className="flex items-baseline justify-between">
        <span className="text-[12px] font-semibold text-ed-text-2">Photos</span>
        <span className="text-[11px] tabular-nums text-ed-text-3">
          {kind === "neck" ? `${photos.filter(hasNeck).length} with a neck · ` : ""}
          {photos.length}
        </span>
      </div>
      <div className={view === "list" ? "flex flex-col gap-1" : "grid grid-cols-3 gap-2"}>
        {list.map((p) => {
          const on = activeId === p.id;
          const dim = kind === "neck" && !hasNeck(p);
          const loading = pendingId === p.id;
          const cls = `${on ? "border-ed-accent ring-2 ring-ed-accent/40" : "border-ed-border hover:border-ed-accent"} ${
            dim ? "opacity-40 hover:opacity-70" : ""
          } ${locked && !loading ? "pointer-events-none" : ""}`;
          const title = dim ? "No neck in this photo" : p.label;
          const spinner = loading ? (
            <span className="absolute inset-0 flex items-center justify-center bg-black/40 text-white">
              <Icon name="progress_activity" size={18} className="animate-spin" />
            </span>
          ) : null;
          return view === "list" ? (
            <button
              key={p.id}
              type="button"
              title={title}
              onClick={() => onPick(p)}
              data-onbody-photo={p.id}
              data-no-neck={dim || undefined}
              aria-pressed={on}
              className={`relative flex items-center gap-2 rounded-lg border bg-white p-1 pr-2 text-left text-[12px] text-ed-text transition ${cls}`}
            >
              <img src={p.thumb} alt="" loading="lazy" decoding="async" className="h-9 w-9 shrink-0 rounded-md object-cover" />
              <span className="truncate">{p.label}</span>
              {spinner}
            </button>
          ) : (
            <button
              key={p.id}
              type="button"
              title={title}
              onClick={() => onPick(p)}
              data-onbody-photo={p.id}
              data-no-neck={dim || undefined}
              aria-pressed={on}
              aria-label={p.label}
              className={`relative aspect-square overflow-hidden rounded-lg border bg-ed-border transition ${cls}`}
            >
              <img src={p.thumb} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" />
              {spinner}
            </button>
          );
        })}
      </div>
    </div>
  );
}
